// =============================================================================
// LocationPicker — modal map for capturing lat/lng + address text
// =============================================================================
// Uses GOOGLE MAPS (map + Places autocomplete + geocoding) when
// VITE_GOOGLE_MAPS_API_KEY is set; otherwise falls back to the free
// OpenStreetMap / Leaflet + Nominatim picker so the app still works without a
// key. Same props + onConfirm({lat,lng,address}) either way, so every call site
// (shops, customers, delivery) is unchanged.

import { type ComponentType, useEffect, useRef, useState } from 'react';
import {
  MapContainer,
  Marker as LeafletMarker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  GoogleMap as GoogleMapBase,
  Marker as GoogleMarkerBase,
  Autocomplete as AutocompleteBase,
  useJsApiLoader,
  type GoogleMapProps,
  type MarkerProps,
  type AutocompleteProps,
} from '@react-google-maps/api';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// @react-google-maps/api ships class components that TS 5 + @types/react 18.3
// reject as JSX elements ("no 'props' property"). Re-type them as functional
// components via their own constructor prop types — a compile-time-only cast
// that preserves full prop/callback typing and is identical at runtime.
const GoogleMap = GoogleMapBase as unknown as ComponentType<GoogleMapProps>;
const GoogleMarker = GoogleMarkerBase as unknown as ComponentType<MarkerProps>;
const Autocomplete = AutocompleteBase as unknown as ComponentType<AutocompleteProps>;

const GOOGLE_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
const GOOGLE_LIBS: 'places'[] = ['places'];
const MAURITIUS_CENTRE: [number, number] = [-20.348404, 57.552152];
const DEFAULT_ZOOM = 11;
const FOCUSED_ZOOM = 16;

interface LocationPickerProps {
  open: boolean;
  onClose: () => void;
  initial?: { lat?: number | null; lng?: number | null; address?: string | null };
  /** Country bias for search. Default = 'mu' (Mauritius). */
  countryCode?: string;
  title?: string;
  onConfirm: (loc: { lat: number; lng: number; address: string }) => void;
}

export function LocationPicker({
  open,
  onClose,
  initial,
  countryCode = 'mu',
  title = 'Pin location on map',
  onConfirm,
}: LocationPickerProps): JSX.Element | null {
  if (!open) return null;
  return (
    <Modal title={title} size="3xl" onClose={onClose}>
      {GOOGLE_KEY ? (
        <GooglePickerBody
          initial={initial}
          countryCode={countryCode}
          onCancel={onClose}
          onConfirm={onConfirm}
        />
      ) : (
        <LeafletPickerBody
          initial={initial}
          countryCode={countryCode}
          onCancel={onClose}
          onConfirm={onConfirm}
        />
      )}
    </Modal>
  );
}

interface BodyProps {
  initial?: LocationPickerProps['initial'];
  countryCode: string;
  onCancel: () => void;
  onConfirm: LocationPickerProps['onConfirm'];
}

// ─────────────────────────────────────────────────────────────────────────────
// Google Maps implementation
// ─────────────────────────────────────────────────────────────────────────────
function GooglePickerBody({ initial, countryCode, onCancel, onConfirm }: BodyProps): JSX.Element {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_KEY ?? '',
    libraries: GOOGLE_LIBS,
  });

  const initialLat = initial?.lat ?? null;
  const initialLng = initial?.lng ?? null;
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(
    initialLat != null && initialLng != null ? { lat: initialLat, lng: initialLng } : null,
  );
  const [address, setAddress] = useState<string>(initial?.address ?? '');
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const reverseGeocode = (lat: number, lng: number): void => {
    if (address.trim()) return; // don't clobber a manually-entered address
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) setAddress(results[0].formatted_address);
    });
  };

  const setPinAt = (lat: number, lng: number): void => {
    setPin({ lat, lng });
    mapRef.current?.panTo({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const onPlaceChanged = (): void => {
    const place = acRef.current?.getPlace();
    const loc = place?.geometry?.location;
    if (!loc) return;
    const lat = loc.lat();
    const lng = loc.lng();
    setPin({ lat, lng });
    setAddress(place.formatted_address ?? place.name ?? '');
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(FOCUSED_ZOOM);
  };

  const useMyLocation = (): void => {
    if (!navigator.geolocation) {
      setGeoError('Browser does not support location services');
      return;
    }
    setGeoBusy(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPinAt(pos.coords.latitude, pos.coords.longitude);
        setGeoBusy(false);
      },
      (err) => {
        setGeoBusy(false);
        setGeoError(err.code === 1 ? 'Location permission denied' : 'Could not get your location');
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  };

  if (loadError) {
    return (
      <div className="text-sm text-negative py-6">
        Could not load Google Maps. Check the API key and that Maps JavaScript + Places APIs are
        enabled.
      </div>
    );
  }
  if (!isLoaded) return <div className="text-sm text-ink-3 py-10 text-center">Loading map…</div>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-3">
        Search for an address, click the map to drop a pin, or use your current location. The
        address auto-fills from the pin — you can edit it.
      </p>

      <Autocomplete
        onLoad={(ac) => {
          acRef.current = ac;
          ac.setComponentRestrictions({ country: countryCode });
          ac.setFields(['geometry', 'formatted_address', 'name']);
        }}
        onPlaceChanged={onPlaceChanged}
      >
        <Input label="Search address" placeholder="e.g. Royal Road, Curepipe…" />
      </Autocomplete>

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" loading={geoBusy} onClick={useMyLocation}>
          Use my location
        </Button>
        {pin && (
          <span className="text-xs text-ink-3 kc-mono">
            {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
          </span>
        )}
        {geoError && <span className="text-xs text-negative">{geoError}</span>}
      </div>

      <div className="rounded-md border border-rule overflow-hidden" style={{ height: 360 }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={pin ?? { lat: MAURITIUS_CENTRE[0], lng: MAURITIUS_CENTRE[1] }}
          zoom={pin ? FOCUSED_ZOOM : DEFAULT_ZOOM}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onClick={(e) => {
            if (e.latLng) setPinAt(e.latLng.lat(), e.latLng.lng());
          }}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        >
          {pin && <GoogleMarker position={pin} />}
        </GoogleMap>
      </div>

      <div>
        <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">
          Address
        </label>
        <textarea
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Auto-filled from pin; edit to refine"
          rows={2}
          className="kc-input w-full resize-none"
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="secondary" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="flex-1"
          disabled={!pin}
          onClick={() => pin && onConfirm({ lat: pin.lat, lng: pin.lng, address: address.trim() })}
        >
          Save location
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OpenStreetMap / Leaflet fallback (no API key)
// ─────────────────────────────────────────────────────────────────────────────
interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
}

function LeafletPickerBody({ initial, countryCode, onCancel, onConfirm }: BodyProps): JSX.Element {
  const initialLat = initial?.lat ?? null;
  const initialLng = initial?.lng ?? null;
  const [pin, setPin] = useState<[number, number] | null>(
    initialLat != null && initialLng != null ? [initialLat, initialLng] : null,
  );
  const [address, setAddress] = useState<string>(initial?.address ?? '');
  const [search, setSearch] = useState<string>('');
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  useEffect(() => {
    if (search.trim().length < 3) {
      setSuggestions([]);
      setSearchBusy(false);
      return;
    }
    setSearchBusy(true);
    const t = setTimeout(async () => {
      try {
        const u = new URL('https://nominatim.openstreetmap.org/search');
        u.searchParams.set('format', 'json');
        u.searchParams.set('q', search);
        u.searchParams.set('countrycodes', countryCode);
        u.searchParams.set('limit', '6');
        const r = await fetch(u.toString(), { headers: { Accept: 'application/json' } });
        setSuggestions((await r.json()) as NominatimResult[]);
      } catch {
        setSuggestions([]);
      } finally {
        setSearchBusy(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [search, countryCode]);

  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      const u = new URL('https://nominatim.openstreetmap.org/reverse');
      u.searchParams.set('format', 'json');
      u.searchParams.set('lat', String(lat));
      u.searchParams.set('lon', String(lng));
      u.searchParams.set('zoom', '18');
      const r = await fetch(u.toString(), { headers: { Accept: 'application/json' } });
      return ((await r.json()) as { display_name?: string }).display_name ?? '';
    } catch {
      return '';
    }
  };

  const setPinAt = async (lat: number, lng: number): Promise<void> => {
    setPin([lat, lng]);
    if (!address.trim()) {
      const auto = await reverseGeocode(lat, lng);
      if (auto) setAddress(auto);
    }
  };

  const useMyLocation = (): void => {
    if (!navigator.geolocation) {
      setGeoError('Browser does not support location services');
      return;
    }
    setGeoBusy(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        await setPinAt(pos.coords.latitude, pos.coords.longitude);
        setGeoBusy(false);
      },
      (err) => {
        setGeoBusy(false);
        setGeoError(err.code === 1 ? 'Location permission denied' : 'Could not get your location');
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  };

  const pickSuggestion = (s: NominatimResult): void => {
    setPin([Number(s.lat), Number(s.lon)]);
    setAddress(s.display_name);
    setSearch('');
    setSuggestions([]);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-3">
        Search for an address, drop a pin by clicking the map, or use your current location. The
        address text auto-fills from the pin — you can edit it.
      </p>
      <div className="relative">
        <Input
          label="Search address"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="e.g. Royal Road, Curepipe…"
        />
        {(suggestions.length > 0 || searchBusy) && (
          <div className="absolute z-[1100] left-0 right-0 mt-1 bg-surface border border-rule rounded-md shadow-lg max-h-60 overflow-y-auto">
            {searchBusy && suggestions.length === 0 && (
              <div className="px-3 py-2 text-xs text-ink-3">Searching…</div>
            )}
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pickSuggestion(s)}
                className="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-paper-2 border-b border-rule-2 last:border-b-0"
              >
                {s.display_name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" loading={geoBusy} onClick={useMyLocation}>
          Use my location
        </Button>
        {pin && (
          <span className="text-xs text-ink-3 kc-mono">
            {pin[0].toFixed(6)}, {pin[1].toFixed(6)}
          </span>
        )}
        {geoError && <span className="text-xs text-negative">{geoError}</span>}
      </div>
      <div className="rounded-md border border-rule overflow-hidden" style={{ height: 360 }}>
        <MapContainer
          center={pin ?? MAURITIUS_CENTRE}
          zoom={pin ? FOCUSED_ZOOM : DEFAULT_ZOOM}
          scrollWheelZoom
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LeafletClickHandler onClick={setPinAt} />
          {pin && (
            <>
              <LeafletMarker position={pin} />
              <RecenterTo position={pin} />
            </>
          )}
        </MapContainer>
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">
          Address
        </label>
        <textarea
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Auto-filled from pin; edit to refine"
          rows={2}
          className="kc-input w-full resize-none"
        />
      </div>
      <div className="flex gap-2 pt-2">
        <Button variant="secondary" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={() => pin && onConfirm({ lat: pin[0], lng: pin[1], address: address.trim() })}
          disabled={!pin}
        >
          Save location
        </Button>
      </div>
    </div>
  );
}

function LeafletClickHandler({
  onClick,
}: {
  onClick: (lat: number, lng: number) => void | Promise<void>;
}): null {
  useMapEvents({
    click: (e: L.LeafletMouseEvent) => {
      void onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function RecenterTo({ position }: { position: [number, number] }): null {
  const map = useMap();
  const [lat, lng] = position;
  useEffect(() => {
    map.flyTo([lat, lng], FOCUSED_ZOOM, { animate: true, duration: 0.6 });
  }, [map, lat, lng]);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Static preview — read-only thumbnail shown on records with lat/lng.
// Google Static Maps image when a key is set; Leaflet mini-map otherwise.
// ─────────────────────────────────────────────────────────────────────────────
export function LocationPreview({
  lat,
  lng,
  height = 160,
  className,
}: {
  lat: number;
  lng: number;
  height?: number;
  className?: string;
}): JSX.Element {
  if (GOOGLE_KEY) {
    const src = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=600x${Math.round(height)}&scale=2&markers=color:0x7A4A2B%7C${lat},${lng}&key=${GOOGLE_KEY}`;
    return (
      <div
        className={`rounded-md border border-rule overflow-hidden ${className ?? ''}`}
        style={{ height }}
      >
        <img src={src} alt="Map location" className="w-full h-full object-cover" loading="lazy" />
      </div>
    );
  }
  return (
    <div
      className={`rounded-md border border-rule overflow-hidden ${className ?? ''}`}
      style={{ height }}
    >
      <MapContainer
        center={[lat, lng]}
        zoom={FOCUSED_ZOOM - 1}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        zoomControl={false}
        attributionControl={false}
        className="h-full w-full"
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <LeafletMarker position={[lat, lng]} />
      </MapContainer>
    </div>
  );
}
